/**
 * trialFlow (F-201 / T-152, US-152)
 *
 * The 14-day no-card trial client: starts a trial through the LP route
 * `POST api/usd-catalog`-neighbour `api/usd-billing/trial/start` (T-152-001)
 * and mirrors the server's trial window in localStorage so the dashboard
 * navbar (MarketAwareNavbar/Navbar read `subscriptionStatus === 'trial'`)
 * and the conversion prompt (TrialConversionModal) can react.
 *
 * Server contract (LP usd-billing.js /trial/start):
 *   200 { success, data: { tier, trialDays, trialStartDate, trialEndDate } }
 *   400 { error: 'invalid_tier' }   — tier outside ['essentials','professional']
 *   401 { error: 'AUTH_REQUIRED' }  — no/invalid session token
 *   409 { error: 'already_active' | 'already_on_trial', message }
 *
 * Rulings (F-201 dispatch 2026-10-08): trial = 14d (copy constant
 * FIRST_FRUITS_TRIAL_DAYS); no credit card, no FastSpring involvement in the
 * trial path — checkout stays the paid path.
 */

import { landlordApi } from './api';
import { FIRST_FRUITS_TRIAL_DAYS } from './usdPricingModel';
import { trackCommitmentStarted } from './analytics';

/** Tiers the LP route accepts as trial startable (mirrors TRIAL_STARTABLE_TIERS). */
export const TRIALABLE_TIERS = ['essentials', 'professional'] as const;

export const SUBSCRIPTION_STATUS_KEY = 'subscriptionStatus';
export const TRIAL_TIER_KEY = 'trialTier';
export const TRIAL_START_KEY = 'trialStartDate';
export const TRIAL_END_KEY = 'trialEndDate';

const DAY_MS = 24 * 60 * 60 * 1000;

export interface TrialState {
  tier: string;
  trialStartDate: string;
  trialEndDate: string;
  /** Whole days remaining, ceil (≤ 0 = expired). */
  daysLeft: number;
  expired: boolean;
}

export type TrialStartStatus = 'started' | 'needs-signup' | 'conflict' | 'error';

export interface TrialStartOutcome {
  status: TrialStartStatus;
  tier?: string;
  trialDays?: number;
  trialStartDate?: string;
  trialEndDate?: string;
  message?: string;
}

export function persistTrialState(input: {
  tier: string;
  trialStartDate: string;
  trialEndDate: string;
}): void {
  try {
    localStorage.setItem(SUBSCRIPTION_STATUS_KEY, 'trial');
    localStorage.setItem(TRIAL_TIER_KEY, input.tier);
    localStorage.setItem(TRIAL_START_KEY, input.trialStartDate);
    localStorage.setItem(TRIAL_END_KEY, input.trialEndDate);
  } catch {
    /* private mode / quota — trial still valid server-side */
  }
}

/** Read the mirrored trial window; null when no trial was started here. */
export function readTrialState(): TrialState | null {
  try {
    const tier = localStorage.getItem(TRIAL_TIER_KEY);
    const endDate = localStorage.getItem(TRIAL_END_KEY);
    const startDate = localStorage.getItem(TRIAL_START_KEY) || '';
    if (!tier || !endDate) return null;
    const end = Date.parse(endDate);
    if (Number.isNaN(end)) return null;
    const daysLeft = Math.ceil((end - Date.now()) / DAY_MS);
    return {
      tier,
      trialStartDate: startDate,
      trialEndDate: endDate,
      daysLeft,
      expired: daysLeft <= 0,
    };
  } catch {
    return null;
  }
}

/** Drop the mirrored trial markers (used by tests / future downgrade paths). */
export function clearTrialState(): void {
  try {
    localStorage.removeItem(SUBSCRIPTION_STATUS_KEY);
    localStorage.removeItem(TRIAL_TIER_KEY);
    localStorage.removeItem(TRIAL_START_KEY);
    localStorage.removeItem(TRIAL_END_KEY);
  } catch {
    /* ignore */
  }
}

export function isTrialableTier(tier: string): boolean {
  return (TRIALABLE_TIERS as readonly string[]).includes(tier);
}

/**
 * Start the 14-day no-card trial for a trialable tier. Never throws: the
 * outcome discriminated union is the contract (PricingPage / PlanComparePage
 * map `needs-signup` → /signup?plan=, `conflict` → inline message).
 */
export async function startFreeTrial(tier: string): Promise<TrialStartOutcome> {
  if (!isTrialableTier(tier)) {
    return { status: 'error', message: 'This tier does not offer a trial.' };
  }
  try {
    const res = await landlordApi.post('api/usd-billing/trial/start', { tier });
    const body = res?.data?.data ?? res?.data ?? {};
    if (res?.data?.success === false) {
      return { status: 'error', message: res?.data?.error || 'TRIAL_START_FAILED' };
    }
    const started: TrialStartOutcome = {
      status: 'started',
      tier: body.tier || tier,
      trialDays: typeof body.trialDays === 'number' ? body.trialDays : FIRST_FRUITS_TRIAL_DAYS,
      trialStartDate: body.trialStartDate || new Date().toISOString(),
      trialEndDate:
        body.trialEndDate ||
        new Date(Date.now() + FIRST_FRUITS_TRIAL_DAYS * DAY_MS).toISOString(),
    };
    persistTrialState({
      tier: started.tier as string,
      trialStartDate: started.trialStartDate as string,
      trialEndDate: started.trialEndDate as string,
    });
    trackCommitmentStarted({ tier: started.tier || tier, channel: 'trial' });
    return started;
  } catch (err: any) {
    const status: number | undefined = err?.response?.status;
    const serverError: string | undefined = err?.response?.data?.error;
    const serverMessage: string | undefined = err?.response?.data?.message;
    if (status === 401 || serverError === 'AUTH_REQUIRED') {
      return { status: 'needs-signup' };
    }
    if (status === 409) {
      return {
        status: 'conflict',
        message:
          serverMessage ||
          'You already have an active trial or subscription.',
      };
    }
    if (status === 400) {
      return {
        status: 'error',
        message: serverMessage || 'This tier does not offer a trial.',
      };
    }
    return { status: 'error', message: serverMessage || err?.message || 'Could not start your trial.' };
  }
}
